import useFormatter from "@/hooks/use-formatter.hook.ts";
import {
  ApiTimeOffCategoryType,
  ApiTimeOffPeriodTrackingTime,
} from "@/types/time-off/time-off-policy.types.ts";
import { ApiTimeOffHistory } from "@/types/time-off/time-off.types.ts";
import { Stack, Text, Tooltip, TooltipContent, TooltipTrigger } from "@skailer-com/uikit";
import { startOfMonth } from "date-fns";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export function TimeOffHistoryChart({ history }: { history: ApiTimeOffHistory }) {
  const { renderDate } = useFormatter();
  const { t } = useTranslation();

  const isHours =
    history?.filter.time_off_category.period_track_type === ApiTimeOffPeriodTrackingTime.hours;
  const pluralKey = isHours ? "plurals:plural_hours" : "plurals:plural_day";

  const chartMeta = useMemo(() => {
    const balances = history.chart.map((item) => item.balance);
    const max = Math.max(...balances);
    const min = Math.min(...balances);

    return {
      max,
      min,
      delta: max - min,
      startPercent: min == 0 ? 10 : 33,
    };
  }, [history.chart]);

  const firstDayOfCurrentMonth = startOfMonth(new Date());

  return (
    <Stack direction={"horizontal"} className={"h-44"} gap={"md"}>
      {chartMeta &&
        history?.chart.map((item) => {
          const isPast = new Date(item.date_period) <= firstDayOfCurrentMonth;
          const circleClass = isPast ? "fa-solid fa-circle" : "fa-light fa-circle";
          return (
            <div className={"h-full min-w-0 flex-1"} key={`${item.date_period}`}>
              <Tooltip delay={false}>
                <div className={"w-full flex items-end grow h-40"}>
                  <TooltipTrigger asChild={true}>
                    <div
                      className={twMerge(
                        "w-full rounded-tl-md rounded-tr-md",
                        new Date() < item.date_period ? "bg-dark/20" : "bg-dark"
                      )}
                      style={{
                        height: `${chartMeta.startPercent + (chartMeta.delta > 0 ? ((item.balance - chartMeta.min) * (100 - chartMeta.startPercent)) / (chartMeta.max - chartMeta.min) : 0)}%`,
                      }}
                    ></div>
                  </TooltipTrigger>
                </div>
                <TooltipContent>
                  <Stack className={"py-1 px-2"}>
                    <Text className={"pb-1"} size={"md"}>
                      {renderDate(item.date_period, "MMM yyyy")}
                    </Text>
                    {item.accrual_amount && item.accrual_amount > 0 && (
                      <div className={"text-sm flex items-center gap-1"}>
                        <i className={`${circleClass} text-(--color-positive)`} />
                        {isPast ? (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: t("time_off:units_accrued", {
                                units: t(pluralKey, { count: item.accrual_amount }),
                              }),
                            }}
                          />
                        ) : (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: t("time_off:units_to_accrue", {
                                units: t(pluralKey, { count: item.accrual_amount }),
                              }),
                            }}
                          />
                        )}
                      </div>
                    )}
                    {item.spend_amount && item.spend_amount > 0 && (
                      <div className={"text-sm flex items-center gap-1"}>
                        <i className={`${circleClass} text-(--color-warning)`} />
                        <span
                          dangerouslySetInnerHTML={{
                            __html: t("time_off:units_spent", {
                              units: t(pluralKey, { count: item.spend_amount }),
                            }),
                          }}
                        />
                      </div>
                    )}
                    {item.book_amount && item.book_amount > 0 && (
                      <div className={"text-sm flex items-center gap-1"}>
                        <i className={`${circleClass} text-(--color-gray-400)`} />
                        <span
                          dangerouslySetInnerHTML={{
                            __html: t("time_off:units_booked", {
                              units: t(pluralKey, { count: item.book_amount }),
                            }),
                          }}
                        />
                      </div>
                    )}
                    {item.expired_amount && item.expired_amount > 0 && (
                      <div className={"text-sm flex items-center gap-1"}>
                        <i className={`${circleClass} text-(--color-danger)`} />
                        {isPast ? (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: t("time_off:units_expired", {
                                units: t(pluralKey, { count: item.expired_amount }),
                              }),
                            }}
                          />
                        ) : (
                          <span
                            dangerouslySetInnerHTML={{
                              __html: t("time_off:units_to_expire", {
                                units: t(pluralKey, { count: item.expired_amount }),
                              }),
                            }}
                          />
                        )}
                      </div>
                    )}
                    {history?.filter.time_off_category.category_type !==
                      ApiTimeOffCategoryType.flexible && (
                      <Text size={"md"} className={"pt-1"}>
                        {t("time_off:balance")}: {item.balance}
                      </Text>
                    )}
                  </Stack>
                </TooltipContent>
              </Tooltip>
              <div className={"h-5 text-sm text-center text-dark/60 leading-5 uppercase"}>
                {renderDate(item.date_period, "MMM")}
              </div>
            </div>
          );
        })}
    </Stack>
  );
}
